/**
 * A wrapper around FriendPickerSheet that uses the share-product-suggestion
 * edge function instead of the standard shareItem mutation.
 * This is used when sharing product suggestions (not already-saved items).
 */
import { useState } from 'react';
import { getAvatarUrl } from '@/lib/avatars';
import { AnimatePresence } from 'framer-motion';
import { Search, Send, Check, Users, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { useAuth } from '@/hooks/useAuth';
import { useShareSuggestion } from '@/hooks/useShareSuggestion';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

interface SuggestionFriendPickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: {
    name: string;
    url?: string;
    image_url: string;
    price?: number | null;
    currency?: string;
    store?: string;
  } | null;
}

interface Friend {
  id: string;
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
}

export function SuggestionFriendPicker({
  open,
  onOpenChange,
  product,
}: SuggestionFriendPickerProps) {
  const { user } = useAuth();
  const { shareSuggestion, isSharing } = useShareSuggestion();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<string[]>([]);
  const [message, setMessage] = useState('');

  const { data: friends, isLoading: isLoadingFriends } = useQuery({
    queryKey: ['friends', user?.id],
    queryFn: async () => {
      if (!user) return [];
      // Get who I follow
      const { data: followData, error: followError } = await supabase
        .from('follows')
        .select('following_id')
        .eq('follower_id', user.id);
      if (followError) throw followError;
      if (!followData || followData.length === 0) return [];
      const followingIds = followData.map(f => f.following_id);

      // Filter to only mutual followers (they also follow me back)
      const { data: mutualData, error: mutualError } = await supabase
        .from('follows')
        .select('follower_id')
        .eq('following_id', user.id)
        .in('follower_id', followingIds);
      if (mutualError) throw mutualError;
      const mutualIds = mutualData?.map(f => f.follower_id) || [];
      if (mutualIds.length === 0) return [];

      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, user_id, display_name, avatar_url')
        .in('user_id', mutualIds);
      if (profileError) throw profileError;
      return profiles as Friend[];
    },
    enabled: !!user && open,
  });

  const filteredFriends = friends?.filter(friend =>
    friend.display_name?.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  const toggleFriend = (userId: string) => {
    setSelectedFriends(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleSend = async () => {
    if (selectedFriends.length === 0 || !product) {
      toast.error('Select at least one friend');
      return;
    }

    const success = await shareSuggestion(selectedFriends, product, message || undefined);
    if (success) {
      setSelectedFriends([]);
      setMessage('');
      onOpenChange(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <DraggableBottomSheet
          open={open}
          onOpenChange={onOpenChange}
          title={
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Share with Friends on WNTD
            </span>
          }
          height="85vh"
        >
          <div className="flex flex-col h-full px-4 pb-4">
            {/* Item Preview */}
            {product && (
              <div className="flex items-center gap-3 p-3 bg-surface-2 rounded-2xl mb-4 flex-shrink-0">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-14 h-14 rounded-xl object-cover flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground line-clamp-2 text-sm">{product.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {selectedFriends.length > 0
                      ? `Sharing with ${selectedFriends.length} friend${selectedFriends.length > 1 ? 's' : ''}`
                      : 'Select friends to share with'}
                  </p>
                </div>
              </div>
            )}

            {/* Search */}
            <div className="relative mb-4 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search friends..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-surface-2 border-0 rounded-xl"
              />
            </div>

            {/* Friends List */}
            <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1">
              <div className="space-y-2 py-1">
                {isLoadingFriends ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredFriends.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p className="text-muted-foreground">
                      {friends?.length === 0 ? "You haven't followed anyone yet" : 'No friends match your search'}
                    </p>
                  </div>
                ) : (
                  filteredFriends.map(friend => {
                    const isSelected = selectedFriends.includes(friend.user_id);
                    return (
                      <button
                        key={friend.user_id}
                        onClick={(e) => { e.preventDefault(); toggleFriend(friend.user_id); }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl transition-colors ${
                          isSelected
                            ? 'bg-primary/10 ring-2 ring-primary ring-offset-2 ring-offset-background'
                            : 'bg-surface-2 hover:bg-surface-3'
                        }`}
                      >
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={getAvatarUrl(friend.avatar_url, friend.user_id)} />
                          <AvatarFallback className="bg-muted" />
                        </Avatar>
                        <span className="flex-1 text-left font-medium text-foreground">
                          {friend.display_name || 'Anonymous'}
                        </span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-surface-3 text-muted-foreground'
                        }`}>
                          {isSelected && <Check className="w-4 h-4" />}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>

            {/* Message Input */}
            {selectedFriends.length > 0 && (
              <div className="pt-4 flex-shrink-0">
                <Textarea
                  placeholder="Add a message (optional)..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="bg-surface-2 border-0 rounded-xl resize-none"
                  rows={2}
                />
              </div>
            )}

            {/* Send Button */}
            <div className="pt-4 flex-shrink-0">
              <Button
                onClick={(e) => { e.stopPropagation(); handleSend(); }}
                disabled={selectedFriends.length === 0 || isSharing}
                className="w-full h-12 rounded-full"
              >
                {isSharing ? (
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Send className="w-5 h-5 mr-2" />
                )}
                {isSharing
                  ? 'Sending...'
                  : `Send to ${selectedFriends.length || ''} Friend${selectedFriends.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          </div>
        </DraggableBottomSheet>
      )}
    </AnimatePresence>
  );
}
